import { motion } from "framer-motion";
import { Twitter, Send, MessageCircle, Instagram, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import { LINKS, NAV_ITEMS, FOOTER } from "@/data/content";
import { useT, useLanguage } from "@/i18n/LanguageContext";

const SOCIALS = [
  { icon: Twitter, href: LINKS.twitter, label: "Twitter / X" },
  { icon: Send, href: LINKS.telegram, label: "Telegram" },
  { icon: MessageCircle, href: LINKS.discord, label: "Discord" },
  { icon: Instagram, href: LINKS.instagram, label: "Instagram" },
  { icon: Mail, href: LINKS.email, label: "Email" },
];

// Legal links – Terms is live, Privacy + Cookie Policy come next (kept as
// disabled placeholders so the footer slot is reserved).
const LEGAL_HEADING = { en: "Legal", sr: "Pravno" };
const LEGAL_LINKS = [
  { key: "terms", label: { en: "Terms & Conditions", sr: "Uslovi korišćenja" }, to: "/terms", ready: true },
  { key: "purchase", label: { en: "Terms of Purchase", sr: "Uslovi kupovine" }, to: "/purchase-terms", ready: true },
  { key: "privacy", label: { en: "Privacy Policy", sr: "Politika privatnosti" }, to: "/privacy", ready: true },
  { key: "cookies", label: { en: "Cookie Policy", sr: "Politika kolačića" }, to: "/cookies", ready: true },
  { key: "contact", label: { en: "Contact", sr: "Kontakt" }, to: "mailto:hello@winfieldtoken.com", ready: true, external: true },
];
const COMING_SOON = { en: "coming soon", sr: "uskoro" };

export const Footer = () => {
  const t = useT();
  const { lang } = useLanguage();

  return (
    <footer data-testid="footer-section" className="relative border-t border-[#E8E7E2] pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid md:grid-cols-4 gap-12"
        >
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/img/winn-coin.png"
                alt="Winfield Token logo"
                className="w-12 h-12 object-contain"
              />
              <span className="font-heading font-semibold text-xl tracking-tight">Winfield</span>
            </div>
            <p className="text-sm text-[#62646B] leading-relaxed mt-4 max-w-xs">{t(FOOTER.tagline)}</p>
          </div>

          <div data-testid="footer-links">
            <h4 className="font-heading font-medium text-sm uppercase tracking-widest text-[#62646B]">
              {t(FOOTER.exploreLabel)}
            </h4>
            <ul className="mt-4 space-y-3">
              {NAV_ITEMS.map((item) => (
                <li key={item.id}>
                  <a href={item.href} className="text-sm text-[#62646B] hover:text-[#0A0A0F] transition-colors duration-300">
                    {t(item.label)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div data-testid="footer-legal">
            <h4 className="font-heading font-medium text-sm uppercase tracking-widest text-[#62646B]">
              {LEGAL_HEADING[lang]}
            </h4>
            <ul className="mt-4 space-y-3">
              {LEGAL_LINKS.map((l) => {
                const label = l.label[lang];
                if (!l.ready) {
                  return (
                    <li key={l.key}>
                      <span
                        data-testid={`footer-legal-${l.key}`}
                        aria-disabled="true"
                        className="text-sm text-[#9B9DA4] inline-flex items-baseline gap-2 cursor-not-allowed"
                      >
                        {label}
                        <em className="text-[10px] not-italic uppercase tracking-widest">
                          ({COMING_SOON[lang]})
                        </em>
                      </span>
                    </li>
                  );
                }
                if (l.external) {
                  return (
                    <li key={l.key}>
                      <a
                        href={l.to}
                        data-testid={`footer-legal-${l.key}`}
                        className="text-sm text-[#62646B] hover:text-[#0A0A0F] transition-colors duration-300"
                      >
                        {label}
                      </a>
                    </li>
                  );
                }
                return (
                  <li key={l.key}>
                    <Link
                      to={l.to}
                      data-testid={`footer-legal-${l.key}`}
                      className="text-sm text-[#62646B] hover:text-[#0A0A0F] transition-colors duration-300"
                    >
                      {label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div data-testid="footer-social">
            <h4 className="font-heading font-medium text-sm uppercase tracking-widest text-[#62646B]">
              {t(FOOTER.communityLabel)}
            </h4>
            <div className="flex flex-wrap gap-3 mt-4">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase().replace(/[^a-z]/g, "")}`}
                  className="w-11 h-11 rounded-full border border-[#E8E7E2] flex items-center justify-center text-[#62646B] hover:text-[#0A0A0F] hover:border-[#0A0A0F] transition-colors duration-300"
                >
                  <s.icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        <p className="font-heading text-[18vw] md:text-[12rem] leading-none font-bold text-[#0E0F13] select-none text-center mt-12 -mb-4 md:-mb-10 overflow-hidden">
          WINN
        </p>

        <div className="border-t border-[#E8E7E2] pt-6 mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-[#62646B]">
            © {new Date().getFullYear()} {t(FOOTER.copyright)}
          </p>
          <p className="text-xs text-[#9B9DA4] max-w-md text-right">{t(FOOTER.disclaimer)}</p>
        </div>

        {/* Persistent regulatory disclaimer (required on every page) */}
        <div
          data-testid="footer-persistent-disclaimer"
          className="mt-6 p-5 rounded-2xl border border-[#E8E7E2] bg-[#F4F2EC]/60"
        >
          <p className="text-xs text-[#62646B] leading-relaxed">{t(FOOTER.persistentDisclaimer)}</p>
        </div>
      </div>
    </footer>
  );
};
